import { z } from 'zod'

export const forgotPasswordRequestSchema = z.object({
  email: z.string().trim().email('Enter a valid email address').max(254),
})

export const resetPasswordRequestSchema = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'This reset link is invalid. Request a new link.'),
  password: z.string().min(8, 'Use at least 8 characters')
    .refine((value) => new TextEncoder().encode(value).length <= 72, 'Use at most 72 UTF-8 bytes'),
  confirmPassword: z.string().min(1, 'Confirm your new password'),
}).refine((value) => value.password === value.confirmPassword, {
  path: ['confirmPassword'], message: 'Passwords do not match',
})

export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

export const workspaceLoginRequestSchema = z.object({
  // Identity comes from loginToken (right after /auth/login) or from the bearer access token
  // (workspace switch). userId is only a cross-check on the server.
  userId: z.string().uuid().optional(),
  workspaceId: z.string().uuid(),
  loginToken: z.string().min(1).optional(),
})

export const loginResponseSchema = z.object({
  userId: z.string().uuid(),
  /** Short-lived (10 min) proof of this login; exchanged for an access token. */
  loginToken: z.string().min(1),
  workspaces: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
    })
  ),
})

export const workspaceLoginResponseSchema = z.object({
  accessToken: z.string(),
  superUser: z.boolean().default(false),
  developerRole: z.boolean().default(false),
  adminRole: z.boolean().default(false),
})

export const refreshResponseSchema = z.object({
  accessToken: z.string(),
  superUser: z.boolean().default(false),
  developerRole: z.boolean().default(false),
  adminRole: z.boolean().default(false),
  userId: z.string().uuid().nullish(),
  email: z.string().nullish(),
  workspaceId: z.string().uuid().nullish(),
})

export const createAdminUserRequestSchema = z.object({
  fullName: z.string().min(1),
  email: z.string().email(),
  password: z.string().min(8),
})

export const createAdminUserResponseSchema = z.object({
  userId: z.string().uuid(),
  fullName: z.string(),
  email: z.string().email(),
  /** Short-lived proof of this registration; used to create the first workspace. */
  loginToken: z.string().min(1),
})

// ─── Accept-invite combined auth ─────────────────────────────────────────────

export const registerAndAcceptInviteRequestSchema = z.object({
  token: z.string().min(1),
  fullName: z.string().min(1),
  password: z.string().min(8),
})

export const loginAndAcceptInviteRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(1),
})

export const acceptInviteAuthResponseSchema = z.object({
  accessToken: z.string(),
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  superUser: z.boolean().default(false),
  developerRole: z.boolean().default(false),
  adminRole: z.boolean().default(false),
})

export type RegisterAndAcceptInviteRequest = z.infer<typeof registerAndAcceptInviteRequestSchema>
export type LoginAndAcceptInviteRequest = z.infer<typeof loginAndAcceptInviteRequestSchema>
export type AcceptInviteAuthResponse = z.infer<typeof acceptInviteAuthResponseSchema>

export type LoginRequest = z.infer<typeof loginRequestSchema>
export type WorkspaceLoginRequest = z.infer<typeof workspaceLoginRequestSchema>
export type LoginResponse = z.infer<typeof loginResponseSchema>
export type WorkspaceLoginResponse = z.infer<typeof workspaceLoginResponseSchema>
export type RefreshResponse = z.infer<typeof refreshResponseSchema>
export type CreateAdminUserRequest = z.infer<typeof createAdminUserRequestSchema>
export type CreateAdminUserResponse = z.infer<typeof createAdminUserResponseSchema>
